import { useMutation } from "@apollo/client";
import {
  Button,
  ButtonGroup,
  ControlGroup,
  NumericInput,
  Tag,
} from "@blueprintjs/core";
import { Select2 } from "@blueprintjs/select";
import _, { isEqual } from "lodash";
import { useEffect, useState } from "react";
import {
  ArticleParameter,
  UpdateArticleParameterInput,
} from "../../../../api/schema";
import { valid_number_keys } from "../../../../components/form/builder/OrderPositions";
import { ValuesVariables } from "../../../../components/form/InputForm";
import { resolve_lang, UserLanguage } from "../../../../lang/lang";
import { AppToaster } from "../../../Template";
import {
  DeleteArticleParameterMutation,
  DeleteArticleParameterResponse,
  UpdateArticleParameterMutation,
} from "../../helper/graphql";
import { SuffixMenuItemRenderer } from "../../helper/SelectRenderer";
import { SimpleButton } from "../../ui/SimpleButton";

interface FloatFieldProps {
  parameter: ArticleParameter;
  on_changed: () => void;
}

export const FloatField = (props: FloatFieldProps) => {
  const [delete_parameter_mutation] =
    useMutation<DeleteArticleParameterResponse>(DeleteArticleParameterMutation);

  const [update_parameter_mutation] = useMutation<
    ArticleParameter,
    ValuesVariables<UpdateArticleParameterInput>
  >(UpdateArticleParameterMutation);

  console.log("building float field", props);

  const article_parameter = props.parameter;
  const key_field = article_parameter.key_field;

  const [submitting, set_submitting] = useState(false);

  const [field_value, set_field_value] = useState(article_parameter.value);
  const [field_suffix, set_field_suffix] = useState(article_parameter.suffix);

  const [is_value_edited, set_value_edited] = useState(false);
  const [is_suffix_edited, set_suffix_edited] = useState(false);

  useEffect(() => {
    set_value_edited(!isEqual(article_parameter.value, field_value));
    set_suffix_edited(!isEqual(article_parameter.suffix, field_suffix));
  }, [
    field_value,
    field_suffix,
    article_parameter.value,
    article_parameter.suffix,
  ]);

  const save_parameter = () => {
    if (!(is_value_edited || is_suffix_edited)) return;

    set_submitting(true);

    const variables: ValuesVariables<UpdateArticleParameterInput> = {
      values: {
        id: article_parameter.id,
      },
    };

    if (is_value_edited) variables.values.value = field_value;
    if (is_suffix_edited) variables.values.suffix = field_suffix;

    update_parameter_mutation({ variables }).then((res) => {
      console.log("update_article_parameter", res);
      if (res.data) {
        AppToaster.show({
          message: UserLanguage().results.saved,
          intent: "success",
          icon: "tick",
        });
        set_submitting(false);
        props.on_changed();
      }
    });
  };

  const remove_parameter = () => {
    if (article_parameter.key_field.required) return;

    delete_parameter_mutation({ variables: { id: article_parameter.id } }).then(
      (res) => {
        console.log("delete_article_parameter", res);
        if (res.data) {
          AppToaster.show({
            message: UserLanguage().results.deleted,
            intent: "success",
            icon: "tick",
          });
          props.on_changed();
        }
      }
    );
  };

  return (
    <ControlGroup style={{ marginBottom: 10 }}>
      <Tag
        large
        intent="none"
        minimal
        style={{ flexShrink: 0, marginRight: 5 }}
      >
        {resolve_lang(
          article_parameter.key_field.name,
          UserLanguage().data.fields
        )}
      </Tag>
      <NumericInput
        disabled={submitting}
        allowNumericCharactersOnly={false}
        asyncControl
        minorStepSize={0.0001}
        rightElement={
          is_value_edited ? (
            <Button
              icon="reset"
              intent="warning"
              outlined
              onClick={() => set_field_value(article_parameter.value)}
            />
          ) : undefined
        }
        onValueChange={(e) => {
          console.log("onValueChange", e);
          if (!isNaN(e)) set_field_value(e.toString());
        }}
        onBlur={(e) => {
          try {
            const n = parseFloat(e.currentTarget.value.split(",").join("."));
            if (!isNaN(n)) set_field_value(n.toString());
          } catch {}
        }}
        onKeyDown={(e) => {
          const value = (e.target as HTMLInputElement).value;
          if (e.key === "-" && value.length !== 0) e.preventDefault();

          if (
            (e.key === "." || e.key === ",") &&
            (_.indexOf(value, ".") !== -1 || _.indexOf(value, ",") !== -1)
          )
            e.preventDefault();

          if (_.indexOf(valid_number_keys, e.key) === -1) {
            e.preventDefault();
            return;
          }
          if (e.key === "Enter" || e.key === "Tab") {
            try {
              const n = parseFloat(e.currentTarget.value.split(",").join("."));
              if (!isNaN(n)) set_field_value(n.toString());
            } catch {}
          }
        }}
        value={field_value}
        fill
      />
      {!article_parameter.key_field.values &&
      article_parameter.key_field.unit ? (
        <Button
          text={_.get(
            UserLanguage().data.units,
            article_parameter.key_field.unit,
            article_parameter.key_field.unit
          )}
          key={"kf_unit_" + article_parameter.key_field.id}
          disabled
        />
      ) : (
        <Select2<string>
          disabled={submitting}
          items={key_field.values}
          itemRenderer={SuffixMenuItemRenderer}
          onItemSelect={(i) => set_field_suffix(i)}
          filterable={false}
        >
          <Button text={field_suffix} key={article_parameter.id} fill />
        </Select2>
      )}
      <ButtonGroup>
        <SimpleButton
          disabled={submitting}
          enabled={is_suffix_edited || is_value_edited}
          intent="success"
          onClick={save_parameter}
          icon="floppy-disk"
        />
        <SimpleButton
          disabled={submitting}
          enabled={!article_parameter.key_field.required}
          intent="danger"
          onClick={remove_parameter}
          icon="cross"
        />
      </ButtonGroup>
    </ControlGroup>
  );
};
